/**
 * `CopyMoveDropOverlay` — the CCM "smoothest UX" drag-to-destination headline
 * (handoff §1.1). Mounted ONCE near the app shell; invisible until a
 * `sources/` resource drag starts (driven by `useCopyMoveDragStore`). While
 * dragging it reveals a centered drop surface listing the lawyer's valid
 * destinations (recent first, §1.3) — drop on one to MOVE (default), hold
 * ⌥/Alt or Ctrl to COPY. The live copy badge reflects the held modifier.
 *
 * Accessibility (handoff §1.1): drag is an ENHANCEMENT, never the only path —
 * the per-row "Send to…" picker (`SendToPicker`) + the H#4 dialog cover the
 * keyboard / no-drag flows. The overlay is `aria-hidden` (a pointer gesture
 * surface) and adds no keyboard trap.
 *
 * Collision (§1.6): on drop, if the destination's CACHED workspace tree
 * already holds a same-path file, a confirm gate (`replace?`) intercepts
 * before the RPC. Default = cancel (the safe choice). When the dest tree
 * isn't cached we can't know — the op proceeds (the picker/dialog still show
 * the at-a-glance collision NOTE).
 */

import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Copy, FilesIcon, Globe, MoveRight } from "lucide-react";
import {
  useCopyMoveDragStore,
  COPY_MOVE_DRAG_MIME,
} from "@/stores/useCopyMoveDragStore";
import { useCases } from "@/hooks/useCases";
import {
  buildCopyMoveTargets,
  targetKeySegment,
  type CopyMoveTarget,
  type SourceScope,
} from "@/lib/copy-move-targets";
import {
  recentTargetsForSource,
  useRecentDestinationsStore,
} from "@/stores/useRecentDestinationsStore";
import { destinationHasCollision } from "@/lib/copy-move-collision";
import { useCopyMoveAction, type RunCopyMoveArgs } from "@/hooks/useCopyMoveAction";
import { useToastStore } from "@/stores/useToastStore";
import type { CopyMoveSource } from "@/components/workspace/ResourceCopyMoveDialog";
import type { CaseWorkspaceTreeResult } from "@/types/gateway";
import { cn } from "@/lib/utils";

function sourceScopeOf(source: CopyMoveSource): SourceScope {
  return source.scope === "org"
    ? { scope: "org" }
    : { scope: "case", caseId: source.caseId };
}

export function CopyMoveDropOverlay() {
  const { t } = useTranslation();
  const drag = useCopyMoveDragStore((s) => s.drag);
  const copyModifier = useCopyMoveDragStore((s) => s.copyModifier);
  const setCopyModifier = useCopyMoveDragStore((s) => s.setCopyModifier);
  const endDrag = useCopyMoveDragStore((s) => s.end);
  const recent = useRecentDestinationsStore((s) => s.recent);
  const { data: cases } = useCases({ enabled: drag != null });
  const { run } = useCopyMoveAction();
  const addToast = useToastStore((s) => s.addToast);
  const queryClient = useQueryClient();
  const orgLabel = t("resourceCopyMove.globalLabel");

  // The drag/drop path has no inline error surface (the overlay is gone the
  // instant the drop lands), so a FORWARD failure — which `run` returns as
  // `{ok:false}` without toasting — would otherwise be silent. Fire the op and
  // surface any failure as an error toast (success + move-undo toast are
  // already dropped by `run`).
  const runWithToast = (op: RunCopyMoveArgs) => {
    void run(op).then((res) => {
      if (!res.ok && res.error) {
        addToast({ text: res.error, kind: "error", autoDismissMs: 6000 });
      }
    });
  };

  // Pending collision confirm (drop landed on a target that already holds a
  // same-path file). Self-contained — it captures the FULL op (path / name /
  // source / target / mode) so it outlives the drag session being cleared
  // (the drag store is reset the moment the drop lands, but the confirm must
  // still render + be able to proceed on "Replace").
  const [collision, setCollision] = useState<{
    path: string;
    name: string;
    source: CopyMoveSource;
    target: CopyMoveTarget;
    mode: "copy" | "move";
  } | null>(null);

  const srcScope = useMemo<SourceScope | null>(
    () => (drag ? sourceScopeOf(drag.source) : null),
    [drag],
  );

  const targets = useMemo(() => {
    if (!srcScope) return [];
    return buildCopyMoveTargets(cases ?? [], srcScope, orgLabel);
  }, [cases, srcScope, orgLabel]);

  const recentTargets = useMemo(() => {
    if (!srcScope) return [];
    const valid = recentTargetsForSource(recent, srcScope);
    const addressable = new Set(targets.map((tg) => tg.caseId));
    return valid.filter((d) => addressable.has(d.caseId)).slice(0, 3);
  }, [recent, srcScope, targets]);

  // Track the COPY modifier globally while a drag is active — the DragEvent's
  // modifier keys aren't reliably readable on the overlay's own render, so we
  // mirror them into the store.
  useEffect(() => {
    if (!drag) return;
    function onKey(e: KeyboardEvent) {
      setCopyModifier(e.altKey || e.ctrlKey);
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);
    function onDragEnd() {
      endDrag();
    }
    window.addEventListener("dragend", onDragEnd);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
      window.removeEventListener("dragend", onDragEnd);
    };
  }, [drag, setCopyModifier, endDrag]);

  // Render the drag overlay while a drag is active, OR the collision confirm
  // while one is pending (the latter outlives the drag — see `collision`).
  if ((!drag || !srcScope) && !collision) return null;

  const mode: "copy" | "move" = copyModifier ? "copy" : "move";

  /** Look up the destination scope's CACHED workspace tree for a collision
   *  pre-check. Keyed exactly like `useCaseWorkspaceTree` (segment = "org" or
   *  the dest caseId). Undefined when not cached → unknown collision. */
  function destTreeFor(target: CopyMoveTarget): CaseWorkspaceTreeResult | undefined {
    return queryClient.getQueryData<CaseWorkspaceTreeResult>([
      "case-workspace-tree",
      targetKeySegment(target),
    ]);
  }

  function handleDrop(target: CopyMoveTarget, e: React.DragEvent) {
    if (!drag) return;
    e.preventDefault();
    // Capture the op BEFORE clearing the drag store (the values come from the
    // live drag session). Re-read the COPY modifier from the drop event itself
    // (authoritative at the moment of release).
    const dropMode: "copy" | "move" = e.altKey || e.ctrlKey ? "copy" : "move";
    const op = {
      path: drag.path,
      name: drag.name,
      source: drag.source,
      target,
      mode: dropMode,
    };
    const tree = destTreeFor(target);
    const hasCollision = tree != null && destinationHasCollision(tree.root, op.path);
    endDrag();
    if (hasCollision) {
      // Gate behind the Replace confirm (§1.6) — default is the safe cancel.
      setCollision(op);
      return;
    }
    runWithToast(op);
  }

  return (
    <>
      {drag && srcScope && (
        <div
          data-testid="copy-move-drop-overlay"
          aria-hidden="true"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-coffee-900/30 backdrop-blur-[2px] dark:bg-coffee-950/50"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            // Drop on the backdrop (not a target) = cancel.
            e.preventDefault();
            endDrag();
          }}
        >
          <div className="w-[min(92vw,420px)] rounded-2xl border border-cream-200/60 bg-white p-4 shadow-[0_8px_40px_rgba(28,24,20,0.18)] dark:border-coffee-700/60 dark:bg-coffee-900">
            <div className="mb-3 flex items-center gap-2">
              {mode === "copy" ? (
                <Copy size={16} className="text-gold-600 dark:text-gold-400" />
              ) : (
                <MoveRight size={16} className="text-gold-600 dark:text-gold-400" />
              )}
              <div className="min-w-0">
                <div className="truncate text-body-sm font-medium text-coffee-900 dark:text-cream-100">
                  {drag.name}
                </div>
                <div className="text-caption text-cream-500">
                  {mode === "copy"
                    ? t("resourceCopyMove.dropHintCopy")
                    : t("resourceCopyMove.dropHintMove")}
                </div>
              </div>
            </div>

            {targets.length === 0 ? (
              <div
                data-testid="copy-move-drop-empty"
                className="rounded-lg border border-dashed border-cream-300/70 px-3 py-5 text-center text-caption text-cream-500 dark:border-coffee-700/60"
              >
                {t("resourceCopyMove.noTargets")}
              </div>
            ) : (
              <ul
                data-testid="copy-move-drop-targets"
                className="flex max-h-[50vh] flex-col gap-1 overflow-y-auto"
              >
                {recentTargets.map((tg) => (
                  <DropTarget
                    key={`recent:${tg.caseId}`}
                    target={tg}
                    mode={mode}
                    recent
                    onDrop={(e) => handleDrop(tg, e)}
                  />
                ))}
                {targets
                  .filter(
                    (tg) => !recentTargets.some((r) => r.caseId === tg.caseId),
                  )
                  .map((tg) => (
                    <DropTarget
                      key={`${tg.kind}:${tg.caseId}`}
                      target={tg}
                      mode={mode}
                      onDrop={(e) => handleDrop(tg, e)}
                    />
                  ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {collision && (
        <CollisionConfirm
          name={collision.name}
          destTitle={collision.target.title}
          onCancel={() => setCollision(null)}
          onReplace={() => {
            const pending = collision;
            setCollision(null);
            runWithToast({
              mode: pending.mode,
              path: pending.path,
              name: pending.name,
              source: pending.source,
              target: pending.target,
            });
          }}
        />
      )}
    </>
  );
}

function DropTarget({
  target,
  mode,
  recent,
  onDrop,
}: {
  target: CopyMoveTarget;
  mode: "copy" | "move";
  recent?: boolean;
  onDrop: (e: React.DragEvent) => void;
}) {
  const [over, setOver] = useState(false);
  return (
    <li>
      <button
        type="button"
        // Pointer-only drop zone inside an aria-hidden surface — keep it out
        // of the tab order (the keyboard path is the SendToPicker, never a
        // drop). Avoids a focusable-inside-aria-hidden a11y violation.
        tabIndex={-1}
        data-testid="copy-move-drop-target"
        data-target-kind={target.kind}
        data-target-id={target.caseId}
        data-over={over ? "true" : undefined}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = mode === "copy" ? "copy" : "move";
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          setOver(false);
          onDrop(e);
        }}
        className={cn(
          "flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-body-sm transition-colors",
          over
            ? "border-gold-500/60 bg-gold-500/10 text-coffee-900 dark:text-cream-100"
            : "border-cream-200/60 text-coffee-700 hover:bg-cream-100/50 dark:border-coffee-700/60 dark:text-cream-200 dark:hover:bg-coffee-800/40",
        )}
      >
        {target.kind === "org" ? (
          <Globe size={14} className="shrink-0 text-cream-500" />
        ) : (
          <FilesIcon size={14} className="shrink-0 text-cream-500" />
        )}
        <span className="flex-1 truncate">{target.title}</span>
        {recent && (
          <span className="shrink-0 text-caption uppercase tracking-wide text-cream-500">
            ·
          </span>
        )}
        {over &&
          (mode === "copy" ? (
            <Copy size={13} className="shrink-0 text-gold-600 dark:text-gold-400" />
          ) : (
            <MoveRight size={13} className="shrink-0 text-gold-600 dark:text-gold-400" />
          ))}
      </button>
    </li>
  );
}

/** Pre-flight collision confirm (§1.6) — default to the safe choice (cancel). */
function CollisionConfirm({
  name,
  destTitle,
  onCancel,
  onReplace,
}: {
  name: string;
  destTitle: string;
  onCancel: () => void;
  onReplace: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="copy-move-collision-confirm"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-coffee-900/40"
    >
      <div className="w-[min(92vw,400px)] rounded-2xl border border-cream-200/60 bg-white p-4 shadow-xl dark:border-coffee-700/60 dark:bg-coffee-900">
        <h2 className="mb-1.5 text-body font-semibold text-coffee-900 dark:text-cream-100">
          {t("resourceCopyMove.collisionTitle")}
        </h2>
        <p className="mb-4 text-body-sm text-coffee-600 dark:text-cream-300">
          {t("resourceCopyMove.collisionPrompt", { name, dest: destTitle })}
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            data-testid="copy-move-collision-cancel"
            className="rounded-md px-3 py-1.5 text-body-sm text-cream-500 hover:bg-cream-100/60 dark:text-cream-400 dark:hover:bg-coffee-800/60"
          >
            {t("actions.cancel")}
          </button>
          <button
            type="button"
            onClick={onReplace}
            data-testid="copy-move-collision-replace"
            // "Keep both" is NON-destructive (creates a numbered copy), so it
            // wears the primary/gold affordance — not the danger red that the
            // old "Replace" label carried.
            className="rounded-md bg-gold-500 px-3 py-1.5 text-body-sm font-medium text-coffee-950 hover:bg-gold-400"
          >
            {t("resourceCopyMove.collisionReplace")}
          </button>
        </div>
      </div>
    </div>
  );
}
